import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDemoSafeMutation } from "@/hooks/useDemoGuard";
import { unitsRepository } from "@/repositories/units";
import { useAuth } from "@/hooks/useAuth";
import { useAssignedPropertyIds } from "@/hooks/useEmployeePropertyAssignments";
import { usePermissions } from "@/hooks/usePermissions";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { isValidUUID } from "@/lib/uuid";
import type { Database } from "@/integrations/supabase/types";

import type { OwnerLite } from "@/lib/ownerHelpers";
import type { UnitCommercialFields } from "@/lib/unitCommercialFields";

type UnitRow = Database["public"]["Tables"]["units"]["Row"];
type UnitInsert = Database["public"]["Tables"]["units"]["Insert"];
type UnitUpdate = Database["public"]["Tables"]["units"]["Update"];
type UnitStatus = Database["public"]["Enums"]["unit_status"];
type UnitType = Database["public"]["Enums"]["unit_type"];

export type Unit = UnitRow & { owner?: OwnerLite | null };
export type { UnitType };

/** `UnitCommercialFields` columns exist on `units` (PR #3825) but not yet in the generated Supabase types. */
export interface CreateUnitData extends UnitCommercialFields {
  property_id: string;
  unit_number: string;
  unit_type?: UnitType;
  purpose?: string;
  status?: UnitStatus;
  floor_number?: number;
  area?: number;
  bedrooms?: number;
  bathrooms?: number;
  living_rooms?: number;
  rent_amount?: number;
  sale_price?: number;
  deposit_amount?: number;
  annual_rent?: number;
  description?: string;
  is_furnished?: boolean;
  electricity_meter?: string;
  water_meter?: string;
  key_type?: string;
  key_number?: string;
  owner_id?: string;
  images?: string[];
  amenities?: {
    utilities?: {
      electricity?: string;
      water?: string;
      gas?: string;
    };
    highlights?: {
      counters?: Record<string, number>;
      checkboxes?: Record<string, boolean>;
    };
    legal?: {
      ownershipType?: string;
      ownershipNumber?: string;
      ownershipDate?: string;
      ownershipDateHijri?: string;
      infoRestriction?: string | null;
      mortgageRestriction?: string | null;
      obligations?: string | null;
      propertyDispute?: string | null;
      ownershipDocuments?: string[];
    };
    information?: {
      finishing?: string;
      furnishing_status?: string;
      is_mezzanine?: boolean;
      front_side_length?: number;
      kitchens?: number;
      maid_rooms?: number;
      storage_rooms?: number;
      outdoor_areas?: number;
    };
  };
}

export function useUnits(propertyId?: string, options?: { enabled?: boolean }) {
  const { companyId } = useAuth();
  const { assignedPropertyIds, isLoading: assignmentsLoading } = useAssignedPropertyIds();
  const { hasPermission } = usePermissions();
  const callerEnabled = options?.enabled ?? true;

  // Layer 1 — permission gate (universal). assignedPropertyIds is null for non-employees.
  const canViewUnits = hasPermission("units.view");
  const isEmployee = assignedPropertyIds !== null;

  return useQuery({
    queryKey: ["units", propertyId, companyId, isEmployee ? assignedPropertyIds : "all"],
    queryFn: async () => {
      if (!companyId) return [];
      if (!canViewUnits) return [];
      if (isEmployee && assignedPropertyIds.length === 0) return [];

      // Layer 2 — for employees, always scope by assigned properties.
      try {
        return await unitsRepository.list({
          companyId,
          propertyId,
          assignedPropertyIds: isEmployee ? assignedPropertyIds : null,
        });
      } catch (error) {
        const errorMessage = `Error fetching units: ${(error as { message?: string })?.message || JSON.stringify(error)}`;
        console.error(errorMessage, error);
        throw new Error(errorMessage);
      }
    },
    enabled: callerEnabled && !!companyId && !assignmentsLoading,
  });
}

export function useUnit(id: string | undefined) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["unit", id],
    queryFn: async () => {
      if (!id || !companyId) return null;

      try {
        return await unitsRepository.getById(id, companyId);
      } catch (error) {
        const errorMessage = `Error fetching unit: ${(error as { message?: string })?.message || JSON.stringify(error)}`;
        console.error(errorMessage, error);
        throw new Error(errorMessage);
      }
    },
    // isValidUUID: URL-driven id — see src/lib/uuid.ts (the #3297 class).
    enabled: !!id && !!companyId && isValidUUID(id),
  });
}

export function useCreateUnit() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();
  const { t } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async (unitData: CreateUnitData) => {
      if (!companyId) throw new Error("No company ID");
      
      const { images, amenities, ...restData } = unitData;
      const insertData: UnitInsert = {
        ...restData,
        company_id: companyId,
        unit_type: unitData.unit_type || "apartment",
        status: unitData.status || "available",
        images: images && images.length > 0 ? images : null,
        amenities: amenities || null,
      };
      
      const data = await unitsRepository.create(insertData).catch((error) => {
        console.error("Error creating unit:", error);
        throw error;
      });

      // Fire-and-forget AR↔EN auto-translation for the unit's description.
      // Soft-fails inside the repository — the unit has already saved.
      if (data && typeof (data as { id?: unknown }).id === "string") {
        await unitsRepository.translate((data as { id: string }).id);
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["units"] });
      queryClient.invalidateQueries({ queryKey: ["units-with-contracts"] });
      queryClient.invalidateQueries({ queryKey: ["units-stats"] });
      queryClient.invalidateQueries({ queryKey: ["property", data.property_id] });
      toast.success(t("تم إضافة الوحدة بنجاح", "Unit added successfully"));
    },
    onError: (error) => {
      console.error("Create unit error:", error);
      const errorMessage = error instanceof Error ? error.message : ((error as { message?: string })?.message || String(error));
      toast.error(t("حدث خطأ أثناء إضافة الوحدة", "Error adding unit") + `: ${errorMessage}`);
    },
  });
}

export function useUpdateUnit() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();
  const { t } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async ({ id, images, ...rest }: { id: string } & Partial<CreateUnitData>) => {
      if (!companyId) throw new Error("No company ID");
      
      const updateData: UnitUpdate = {
        ...rest,
        images: images !== undefined ? (images.length > 0 ? images : null) : undefined,
      };
      
      const data = await unitsRepository.update(id, updateData, companyId).catch((error) => {
        console.error("Error updating unit:", error);
        throw error;
      });

      if (!data) throw new Error("Unit not found or access denied");

      // Re-translate ONLY when the description actually changed in this edit.
      if (typeof rest.description === "string") {
        await unitsRepository.translate(id);
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["units"] });
      queryClient.invalidateQueries({ queryKey: ["units-with-contracts"] });
      queryClient.invalidateQueries({ queryKey: ["units-stats"] });
      queryClient.invalidateQueries({ queryKey: ["unit", data.id] });
      toast.success(t("تم تحديث الوحدة بنجاح", "Unit updated successfully"));
    },
    onError: (error) => {
      console.error("Update unit error:", error);
      const errorMessage = error instanceof Error ? error.message : ((error as { message?: string })?.message || String(error));
      toast.error(t("حدث خطأ أثناء تحديث الوحدة", "Error updating unit") + `: ${errorMessage}`);
    },
  });
}

export function useDeleteUnit() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();
  const { t } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async (id: string) => {
      if (!companyId) throw new Error("No company ID");
      
      await unitsRepository.softDelete(id, companyId).catch((error) => {
        console.error("Error deleting unit:", error);
        throw error;
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["units"] });
      queryClient.invalidateQueries({ queryKey: ["units-with-contracts"] });
      queryClient.invalidateQueries({ queryKey: ["units-stats"] });
      toast.success(t("تم حذف الوحدة بنجاح", "Unit deleted successfully"));
    },
    onError: (error) => {
      console.error("Delete unit error:", error);
      const errorMessage = error instanceof Error ? error.message : ((error as { message?: string })?.message || String(error));
      toast.error(t("حدث خطأ أثناء حذف الوحدة", "Error deleting unit") + `: ${errorMessage}`);
    },
  });
}

// Stats
export function useUnitsStats(range?: { startDate?: string; endDate?: string }) {
  const { companyId } = useAuth();
  const { hasPermission } = usePermissions();

  return useQuery({
    queryKey: ["units-stats", companyId, range?.startDate, range?.endDate],
    queryFn: async () => {
      if (!companyId) return { total: 0, available: 0, rented: 0, maintenance: 0 };
      if (!hasPermission("units.view")) return { total: 0, available: 0, rented: 0, maintenance: 0 };

      try {
        return await unitsRepository.statsByStatus(companyId, range);
      } catch (error) {
        const errorMessage = `Error fetching units stats: ${(error as { message?: string })?.message || JSON.stringify(error)}`;
        console.error(errorMessage, error);
        throw new Error(errorMessage);
      }
    },
    enabled: !!companyId,
  });
}
